/**
 * Pure rotation logic (no React, no browser APIs) so it can be unit tested
 * directly and stay the single source of truth for App.jsx and the backend.
 *
 * Zones and the number of weekly blocks are configuration, not constants:
 * zones can be added or removed and shifts can be any length, as long as the
 * shifts still tile 24h.
 */

// Canonical shift times are UTC so coverage stays gap-free year round. Local
// labels are derived per date from `tz`, so they follow DST instead.
export const DEFAULT_ZONES = [
  { name: 'IST', tz: 'Asia/Kolkata', start: '03:00', durationHours: 6 },
  { name: 'CET', tz: 'Europe/Paris', start: '09:00', durationHours: 6 },
  { name: 'EST', tz: 'America/New_York', start: '15:00', durationHours: 6 },
  { name: 'PST', tz: 'America/Los_Angeles', start: '21:00', durationHours: 6 },
];

// Mon-Tue, Wed-Thu, Fri-Sun.
export const DEFAULT_BLOCKS_PER_WEEK = 3;

const ZONE_COLORS = [
  'bg-purple-100 text-purple-800',
  'bg-blue-100 text-blue-800',
  'bg-green-100 text-green-800',
  'bg-orange-100 text-orange-800',
  'bg-pink-100 text-pink-800',
  'bg-teal-100 text-teal-800',
  'bg-amber-100 text-amber-800',
  'bg-indigo-100 text-indigo-800',
];

export const zoneColor = (index) => ZONE_COLORS[index % ZONE_COLORS.length];

const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const WEEK_DAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const MINUTES_PER_DAY = 1440;

const pad = (n) => String(n).padStart(2, '0');

// Minutes since midnight, or NaN when the value is not a valid HH:MM.
export const timeToMinutes = (hhmm) => {
  const [h, m] = String(hhmm).split(':').map(Number);
  if (!Number.isInteger(h) || !Number.isInteger(m)) return NaN;
  if (h < 0 || h > 23 || m < 0 || m > 59) return NaN;
  return h * 60 + m;
};

export const minutesToTime = (mins) => {
  const m = ((Math.round(mins) % MINUTES_PER_DAY) + MINUTES_PER_DAY) % MINUTES_PER_DAY;
  return `${pad(Math.floor(m / 60))}:${pad(m % 60)}`;
};

export const zoneEnd = (zone) =>
  minutesToTime(timeToMinutes(zone.start) + Math.round((zone.durationHours || 0) * 60));

// Follow-the-sun order falls out of the UTC start times. An anchor name just
// rotates the list so that zone is shown first.
export const sortZones = (zones = [], anchorName) => {
  const sorted = [...zones].sort((a, b) => timeToMinutes(a.start) - timeToMinutes(b.start));
  const at = sorted.findIndex((z) => z.name === anchorName);
  return at > 0 ? [...sorted.slice(at), ...sorted.slice(0, at)] : sorted;
};

// Reports every problem at once so the UI can list them.
// A timezone the runtime does not know makes the local-hours column silently
// blank, so it is rejected rather than stored.
export const timezoneIsValid = (tz) => {
  // A missing timeZone means "the machine's own", which Intl accepts happily
  // and which would silently show each viewer a different local time.
  if (typeof tz !== 'string' || !tz.trim()) return false;
  try {
    new Intl.DateTimeFormat('en-US', { timeZone: tz });
    return true;
  } catch {
    return false;
  }
};

export const validateZones = (zones = []) => {
  const errors = [];
  if (zones.length === 0) errors.push('Add at least one zone.');

  const seen = new Set();
  zones.forEach((zone, i) => {
    const label = zone.name || `Zone ${i + 1}`;
    if (!zone.name || !String(zone.name).trim()) errors.push(`Zone ${i + 1} needs a name.`);
    else if (seen.has(zone.name)) errors.push(`Duplicate zone name "${zone.name}".`);
    else seen.add(zone.name);
    if (Number.isNaN(timeToMinutes(zone.start))) errors.push(`"${label}" has an invalid start time.`);
    if (!(zone.durationHours > 0)) errors.push(`"${label}" needs a duration greater than 0.`);
    if (!timezoneIsValid(zone.tz)) errors.push(`"${label}" has an unknown timezone "${zone.tz}".`);
  });
  if (errors.length > 0) return { ok: false, errors };

  const total = zones.reduce((sum, z) => sum + Math.round(z.durationHours * 60), 0);
  if (total !== MINUTES_PER_DAY) {
    errors.push(`Shifts total ${(total / 60).toFixed(2)}h. They must add up to 24h.`);
  }

  const sorted = sortZones(zones);
  sorted.forEach((zone, i) => {
    const next = sorted[(i + 1) % sorted.length];
    if (sorted.length > 1 && zoneEnd(zone) !== next.start) {
      errors.push(`"${zone.name}" ends at ${zoneEnd(zone)} but "${next.name}" starts at ${next.start}.`);
    }
  });

  return { ok: errors.length === 0, errors };
};

// Local window for a zone on a given date, e.g. "8:30am-2:30pm". Derived from
// the zone's timezone so it tracks DST.
export const formatZoneLocalWindow = (zone, dateStr) => {
  const day = parseDate(dateStr);
  if (Number.isNaN(day.getTime()) || !zone?.tz) return '';
  const startMin = timeToMinutes(zone.start);
  if (Number.isNaN(startMin)) return '';

  const at = (offsetMinutes) =>
    new Date(day.getTime() + (startMin + offsetMinutes) * 60_000);
  const show = (date) => {
    try {
      return new Intl.DateTimeFormat('en-US', {
        timeZone: zone.tz,
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
      })
        .format(date)
        .replace(/\s/g, '')
        .toLowerCase();
    } catch {
      return '';
    }
  };

  const from = show(at(0));
  const to = show(at(Math.round((zone.durationHours || 0) * 60)));
  return from && to ? `${from}-${to}` : '';
};

// Minutes a timezone is ahead of UTC at a given instant.
export const zoneOffsetMinutes = (tz, date) => {
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: tz,
      hour12: false,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    })
      .formatToParts(date)
      .reduce((acc, part) => ({ ...acc, [part.type]: part.value }), {});
    const asUtc = Date.UTC(
      parts.year,
      parts.month - 1,
      parts.day,
      parts.hour % 24,
      parts.minute,
      parts.second
    );
    return Math.round((asUtc - date.getTime()) / 60_000);
  } catch {
    return NaN;
  }
};

// Whether a timezone is on daylight saving on that date. Compares against the
// smaller of the January and July offsets, so it also holds south of the
// equator where summer falls in January.
export const isDstAt = (tz, dateStr) => {
  const day = parseDate(dateStr);
  if (Number.isNaN(day.getTime())) return false;
  const year = day.getUTCFullYear();
  const jan = zoneOffsetMinutes(tz, new Date(Date.UTC(year, 0, 1)));
  const jul = zoneOffsetMinutes(tz, new Date(Date.UTC(year, 6, 1)));
  const now = zoneOffsetMinutes(tz, day);
  if ([jan, jul, now].some(Number.isNaN)) return false;
  return now > Math.min(jan, jul);
};

// Parse a YYYY-MM-DD string as UTC to avoid timezone drift.
// Out-of-range parts return an invalid Date rather than rolling over.
export const parseDate = (dateStr) => {
  const [year, month, day] = String(dateStr).split('-').map(Number);
  const date = new Date(Date.UTC(year, month - 1, day));
  const exact =
    date.getUTCFullYear() === year && date.getUTCMonth() === month - 1 && date.getUTCDate() === day;
  return exact ? date : new Date(NaN);
};

// Format a Date as YYYY-MM-DD.
export const formatDate = (date) => date.toISOString().split('T')[0];

// Monday of the week containing date.
export const getMonday = (date) => {
  const d = new Date(date);
  const day = d.getUTCDay();
  const diff = d.getUTCDate() - day + (day === 0 ? -6 : 1);
  d.setUTCDate(diff);
  return d;
};

// Monday is 0, Sunday is 6.
export const dayIndexInWeek = (dayOfWeek) => (dayOfWeek + 6) % 7;

// How many days each weekly block covers. Remainder days go to the later
// blocks, so 3 blocks give Mon-Tue, Wed-Thu, Fri-Sun.
export const getBlockSizes = (blocks = DEFAULT_BLOCKS_PER_WEEK) => {
  const n = Number.isInteger(blocks) ? Math.min(Math.max(blocks, 1), 7) : DEFAULT_BLOCKS_PER_WEEK;
  const base = Math.floor(7 / n);
  const remainder = 7 % n;
  return Array.from({ length: n }, (_, i) => (i >= n - remainder ? base + 1 : base));
};

export const getBlockLabels = (blocks = DEFAULT_BLOCKS_PER_WEEK) => {
  let day = 0;
  return getBlockSizes(blocks).map((size) => {
    const first = WEEK_DAY_LABELS[day];
    const last = WEEK_DAY_LABELS[day + size - 1];
    day += size;
    return size === 1 ? first : `${first}-${last}`;
  });
};

export const getBlockForDayIndex = (dayIndex, blocks = DEFAULT_BLOCKS_PER_WEEK) => {
  const sizes = getBlockSizes(blocks);
  let day = 0;
  for (let i = 0; i < sizes.length; i++) {
    day += sizes[i];
    if (dayIndex < day) return i;
  }
  return sizes.length - 1;
};

const gcd = (a, b) => (b === 0 ? a : gcd(b, a % b));

/**
 * Member index per block per week, as a cycle of `size` weeks.
 *
 * Over one cycle every member takes `blocks` turns and holds each block
 * exactly once. Stride `blocks` walks straight through the roster; when that
 * shares a factor with the roster size it would pin members to one block, so
 * those sizes step by 1 instead.
 */
export const getRotationPattern = (size, blocks = DEFAULT_BLOCKS_PER_WEEK) => {
  const members = Number.isInteger(size) && size >= 1 ? size : 1;
  const count = getBlockSizes(blocks).length;
  const stride = gcd(count, members) === 1 ? count : 1;
  return Array.from({ length: members }, (_, week) =>
    Array.from({ length: count }, (_, block) => (stride * week + block) % members)
  );
};

// Everyone holding a shift in a week's assignments.
export const getSlrPeopleForWeek = (weekAssignments = {}) => {
  const people = new Set();
  Object.values(weekAssignments).forEach((blocks) => {
    (blocks || []).forEach((person) => {
      if (person && person !== '—') people.add(person);
    });
  });
  return people;
};

// Copy sorted by date; never sorts the caller's array in place.
export const sortPtoEntries = (entries = []) =>
  [...entries].sort((a, b) => String(a.date).localeCompare(String(b.date)));

// Entries without the given one, matched by name and date rather than position,
// so removing from a sorted view cannot delete the wrong row.
export const removePtoEntry = (entries = [], entry) =>
  entries.filter((p) => !(p.name === entry?.name && p.date === entry?.date));

// Every date from start to end inclusive, walked in UTC. Mixing local and UTC
// here drops or repeats a day whenever the range crosses a DST change.
export const expandDateRange = (startDate, endDate) => {
  const first = parseDate(startDate);
  const last = parseDate(endDate || startDate);
  if (Number.isNaN(first.getTime()) || Number.isNaN(last.getTime())) return [];

  const dates = [];
  const cursor = new Date(first);
  while (cursor <= last) {
    dates.push(formatDate(cursor));
    cursor.setUTCDate(cursor.getUTCDate() + 1);
  }
  return dates;
};

// A date string shifted by whole days, in UTC.
export const shiftDate = (dateStr, days) => {
  const date = parseDate(dateStr);
  if (Number.isNaN(date.getTime())) return '';
  date.setUTCDate(date.getUTCDate() + days);
  return formatDate(date);
};

// Existing entries plus one per day of the range, skipping days already held
// by that person, including duplicates inside the range itself.
export const addPtoRange = (entries = [], name, startDate, endDate) => {
  if (!name || !startDate) return entries;
  const held = new Set(entries.filter((p) => p.name === name).map((p) => p.date));
  const added = [];
  expandDateRange(startDate, endDate).forEach((date) => {
    if (held.has(date)) return;
    held.add(date);
    added.push({ name, date });
  });
  return added.length > 0 ? [...entries, ...added] : entries;
};

// Key used to store a manual swap for one zone on one day.
export const overrideKey = (date, zoneName) => `${date}-${zoneName}`;

// CLR runs weekly, so its swaps are keyed by the Monday of the week. The
// prefix keeps them out of the per-zone namespace, where a zone happening to
// be called CLR would otherwise collide.
export const CLR_OVERRIDE_PREFIX = 'clr:';
export const clrOverrideKey = (weekStart) => `${CLR_OVERRIDE_PREFIX}${weekStart}`;

export const setOverride = (overrides = {}, date, zoneName, person) => ({
  ...overrides,
  [overrideKey(date, zoneName)]: person,
});

// Every Monday covered by a range, so a CLR swap can span several weeks.
export const weekStartsInRange = (startDate, endDate) => {
  const days = expandDateRange(startDate, endDate);
  return [...new Set(days.map((d) => formatDate(getMonday(parseDate(d)))))];
};

/**
 * Apply a swap across a date range.
 *
 * For a zone that is one entry per day; for CLR it is one entry per week the
 * range touches, since CLR is a whole-week role.
 */
export const setOverrideRange = (overrides = {}, { target, startDate, endDate, person }) => {
  if (!target || !person || !startDate) return overrides;
  const next = { ...overrides };
  if (target === 'CLR') {
    weekStartsInRange(startDate, endDate).forEach((weekStart) => {
      next[clrOverrideKey(weekStart)] = person;
    });
  } else {
    expandDateRange(startDate, endDate).forEach((date) => {
      next[overrideKey(date, target)] = person;
    });
  }
  return next;
};

// Describes what a swap would change, for confirming before it is applied.
export const describeOverrideRange = ({ target, startDate, endDate }) => {
  if (target === 'CLR') {
    const weeks = weekStartsInRange(startDate, endDate);
    return { count: weeks.length, unit: weeks.length === 1 ? 'week' : 'weeks', items: weeks };
  }
  const days = expandDateRange(startDate, endDate);
  return { count: days.length, unit: days.length === 1 ? 'day' : 'days', items: days };
};

export const clearOverride = (overrides = {}, key) => {
  const next = { ...overrides };
  delete next[key];
  return next;
};

export const clearOverrides = (overrides = {}, keys = []) => {
  const next = { ...overrides };
  keys.forEach((key) => delete next[key]);
  return next;
};

/**
 * Collapse stored swaps back into the ranges they were entered as.
 *
 * A range is stored one key per day (or per week for CLR), so consecutive keys
 * with the same target and person are merged into a single row.
 */
export const groupOverrides = (overrides = {}) => {
  const rows = Object.entries(overrides)
    .map(([key, person]) =>
      key.startsWith(CLR_OVERRIDE_PREFIX)
        ? { kind: 'CLR', target: 'CLR', date: key.slice(CLR_OVERRIDE_PREFIX.length), person, key }
        : { kind: 'SLR', target: key.slice(11), date: key.slice(0, 10), person, key }
    )
    .filter((r) => r.target && r.person && !Number.isNaN(parseDate(r.date).getTime()));

  rows.sort(
    (a, b) =>
      a.target.localeCompare(b.target) ||
      a.person.localeCompare(b.person) ||
      a.date.localeCompare(b.date)
  );

  const groups = [];
  rows.forEach((row) => {
    const last = groups[groups.length - 1];
    const step = row.kind === 'CLR' ? 7 : 1;
    if (
      last &&
      last.kind === row.kind &&
      last.target === row.target &&
      last.person === row.person &&
      shiftDate(last.endDate, step) === row.date
    ) {
      last.endDate = row.date;
      last.keys.push(row.key);
      return;
    }
    groups.push({
      kind: row.kind,
      target: row.target,
      person: row.person,
      startDate: row.date,
      endDate: row.date,
      keys: [row.key],
    });
  });

  return groups.sort(
    (a, b) => a.startDate.localeCompare(b.startDate) || a.target.localeCompare(b.target)
  );
};

// The generated day for a date, or null when it falls outside the schedule.
export const findScheduleDay = (schedule = [], dateStr) => {
  if (!dateStr) return null;
  for (const week of schedule) {
    const day = (week.days || []).find((d) => d.date === dateStr);
    if (day) return day;
  }
  return null;
};

// What the swap form needs to know about a date.
export const getDateInfo = (schedule, zoneNames, dateStr) => {
  const day = findScheduleDay(schedule, dateStr);
  if (!day) return null;
  return {
    timezones: zoneNames,
    assignments: day.people,
    block: day.block,
    isWeekend: day.isWeekend,
  };
};

/**
 * Who currently holds a zone (or CLR) over a date range, as contiguous spans.
 *
 * Dates outside the generated schedule are skipped, so an empty result means
 * the range is not covered by the weeks currently shown.
 */
export const currentHolders = (schedule = [], target, startDate, endDate) => {
  if (!target || !startDate) return [];
  const spans = [];
  const push = (person, date) => {
    const last = spans[spans.length - 1];
    if (last && last.person === person) {
      last.endDate = date;
      return;
    }
    spans.push({ person, startDate: date, endDate: date });
  };

  if (target === 'CLR') {
    weekStartsInRange(startDate, endDate).forEach((weekStart) => {
      const week = schedule.find((w) => w.startDate === weekStart);
      if (week) push(week.clr || null, weekStart);
    });
  } else {
    expandDateRange(startDate, endDate).forEach((date) => {
      const day = findScheduleDay(schedule, date);
      if (day) push(day.people?.[target] ?? null, date);
    });
  }
  return spans;
};

// The roster is keyed flat by zone name: { IST: [...], CET: [...] }. An earlier
// build grouped zones under two pools, { A: { IST, EST }, B: { CET, PST } }, so
// flatten that old shape to keep a data directory written before the change
// readable. Idempotent: an already-flat roster (its values are arrays) is
// returned unchanged.
export const normalizeRoster = (roster) => {
  if (!roster || typeof roster !== 'object' || Array.isArray(roster)) return roster;
  const values = Object.values(roster);
  const nested = values.length > 0 && values.every(
    (v) => v && typeof v === 'object' && !Array.isArray(v),
  );
  if (!nested) return roster;
  const flat = {};
  for (const pool of values) {
    for (const [zone, people] of Object.entries(pool)) {
      flat[zone] = (flat[zone] || []).concat(Array.isArray(people) ? people : []);
    }
  }
  return flat;
};

// ---- Team table ----------------------------------------------------------
// The Settings table is the one place people, zones and CLR membership are
// edited, so these translate between its rows and the stored rosters.

// Practical check: one @, no spaces, a dot in the domain.
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
export const emailIsValid = (email) => EMAIL_RE.test(email.trim());

// A row per person: the email is optional, the zone is where they are on call
// and clr is whether they take a turn in the weekly customer lead rotation.
export const teamRows = (map, roster, clrRoster = []) => {
  roster = normalizeRoster(roster);
  const zoneOf = (name) =>
    Object.keys(roster || {}).find((z) => (roster[z] || []).includes(name)) || '';
  const rows = Object.entries(map || {}).map(([email, name]) => ({
    email,
    name,
    zone: zoneOf(name),
    clr: clrRoster.includes(name),
  }));
  // Anyone on a roster without an email still needs a row, or saving would drop
  // them from their zone or from CLR.
  const named = new Set(rows.map((r) => r.name));
  const addMissing = (name, zone) => {
    if (named.has(name)) return;
    named.add(name);
    rows.push({ email: '', name, zone, clr: clrRoster.includes(name) });
  };
  Object.entries(roster || {}).forEach(([zone, people]) =>
    (people || []).forEach((name) => addMissing(name, zone))
  );
  clrRoster.forEach((name) => addMissing(name, ''));
  return rows;
};

// Grouped by zone follow-the-sun, unassigned last. Display order only; the
// rotation orders are preserved separately.
export const sortTeamRows = (rows, zoneNames) =>
  [...rows].sort((a, b) => {
    const rank = (zone) => (zoneNames.indexOf(zone) === -1 ? zoneNames.length : zoneNames.indexOf(zone));
    return rank(a.zone) - rank(b.zone) || a.name.localeCompare(b.name);
  });

export const toEmailMap = (rows) =>
  Object.fromEntries(
    rows.filter((r) => r.email.trim() && r.name.trim()).map((r) => [r.email.trim(), r.name.trim()])
  );

// A zone's order is its rotation order, so people already in the zone keep
// their place and newcomers go to the end. Otherwise editing anything here
// would reshuffle who covers which block.
export const toRoster = (rows, zoneNames, previous = {}) =>
  Object.fromEntries(
    zoneNames.map((zone) => {
      const assigned = rows
        .filter((r) => r.zone === zone && r.name.trim())
        .map((r) => r.name.trim());
      const kept = (previous[zone] || []).filter((name) => assigned.includes(name));
      return [zone, [...kept, ...assigned.filter((name) => !kept.includes(name))]];
    })
  );

// Ticking someone appends them, so the existing rotation order does not shift.
export const toClrRoster = (rows, previous = []) => {
  const ticked = new Set(rows.filter((r) => r.clr && r.name.trim()).map((r) => r.name.trim()));
  const kept = previous.filter((name) => ticked.has(name));
  return [...kept, ...[...ticked].filter((name) => !kept.includes(name))];
};

/**
 * What a zone's size and the blocks per week mean for the people in it.
 *
 * A zone of N people runs an N-week cycle. Each week it needs min(blocks, N)
 * of them, so N - min(blocks, N) are free that week, and by the same count each
 * person gets that many weeks off per cycle. Those free people are the only
 * ones who can take CLR without also holding a shift.
 */
export const projectZone = (people, blocks = DEFAULT_BLOCKS_PER_WEEK) => {
  const size = Number.isInteger(people) && people > 0 ? people : 0;
  const count = getBlockSizes(blocks).length;
  if (size === 0) return { size, cycleWeeks: 0, weeksOnDuty: 0, weeksOff: 0, freePerWeek: 0, blocksPerTurn: 0 };
  const weeksOnDuty = Math.min(count, size);
  return {
    size,
    cycleWeeks: size,
    weeksOnDuty,
    weeksOff: size - weeksOnDuty,
    // Same number: over an N-week cycle the weeks off are spread one per week.
    freePerWeek: size - weeksOnDuty,
    blocksPerTurn: count > size ? Math.ceil(count / size) : 1,
  };
};

/**
 * The absolute UTC window of a zone's shift on a given date.
 *
 * The duration is added to the start, so a shift running past midnight, or one
 * covering a full 24h, needs no special case. Deriving the end clock time and
 * comparing it against the start cannot tell a wrap from a same-hour end.
 */
export const zoneWindowUtc = (zone, dateStr) => {
  const startMinutes = timeToMinutes(zone?.start);
  const minutes = Math.round((zone?.durationHours || 0) * 60);
  const day = parseDate(dateStr);
  if (Number.isNaN(day.getTime()) || Number.isNaN(startMinutes) || minutes <= 0) return null;
  const start = new Date(day.getTime() + startMinutes * 60_000);
  return { start, end: new Date(start.getTime() + minutes * 60_000) };
};

// Everyone on the roster, in zone order, without duplicates.
export const listMembers = (roster, zoneNames = []) =>
  roster ? [...new Set(zoneNames.flatMap((tz) => roster[tz] || []))] : [];

// First and last dates a generated schedule covers, or null when it is empty.
export const getScheduleRange = (schedule = []) => {
  const days = schedule.flatMap((week) => week.days || []);
  if (days.length === 0) return null;
  return { first: days[0].date, last: days[days.length - 1].date };
};

// The Monday the schedule starts on: the first Monday on or after scheduleStart.
export const firstMondayOnOrAfter = (scheduleStart) => {
  const monday = getMonday(parseDate(scheduleStart));
  const start = parseDate(scheduleStart);
  if (monday < start) monday.setUTCDate(monday.getUTCDate() + 7);
  return monday;
};

/**
 * Reconstruct a CLR rotation from the weekly holders read off the calendar, so
 * the (startIndex + weekIndex) rotation reproduces the calendar from the view's
 * first Monday onward without any per-week swaps.
 *
 * `weeks` is [{ weekStart:'YYYY-MM-DD', person }] (the CLR: events). `members`
 * are the current CLR names; any never seen in the window are appended so nobody
 * is dropped. Returns { roster, startIndex, warnings }.
 */
export const clrRotationFromWeeks = (weeks = [], scheduleStart, members = []) => {
  const firstMonday = firstMondayOnOrAfter(scheduleStart);
  const fmStr = formatDate(firstMonday);
  const warnings = [];

  const clean = (weeks || [])
    .filter((w) => w && w.weekStart && w.person)
    .map((w) => ({ weekStart: w.weekStart, person: w.person }))
    .sort((a, b) => a.weekStart.localeCompare(b.weekStart));

  // Prefer the current and future weeks (what the user cares about); only fall
  // back to older events if there are none from the view start onward.
  const fromView = clean.filter((w) => w.weekStart >= fmStr);
  const used = fromView.length ? fromView : clean;
  if (!used.length) {
    return { roster: members.slice(), startIndex: 0, warnings: ['No CLR events found on the calendar.'] };
  }

  const weekIndex = (dateStr) =>
    Math.round((parseDate(dateStr).getTime() - firstMonday.getTime()) / (7 * 86_400_000));

  const order = [];
  for (const w of used) if (!order.includes(w.person)) order.push(w.person);
  for (const m of members) {
    if (!order.includes(m)) {
      order.push(m);
      warnings.push(`${m} was not on the calendar; added to the end of the rotation.`);
    }
  }

  const n = order.length;
  const mod = (x) => ((x % n) + n) % n;
  const startIndex = mod(-weekIndex(used[0].weekStart));

  for (const w of used) {
    const predicted = order[mod(startIndex + weekIndex(w.weekStart))];
    if (predicted !== w.person) {
      warnings.push(`${w.weekStart}: calendar shows ${w.person} but the rotation gives ${predicted} (a past swap?).`);
    }
  }

  return { roster: order, startIndex, warnings };
};

/**
 * Build the week-by-week schedule.
 *
 * Returns [] when there is no roster or the start date is unusable. Each week
 * carries per-zone block assignments, a day-by-day expansion and the CLR.
 */
export const generateSchedule = ({
  scheduleStart,
  weeksToShow,
  roster,
  zones = DEFAULT_ZONES,
  blocksPerWeek = DEFAULT_BLOCKS_PER_WEEK,
  anchorZone,
  ptoEntries = [],
  overrides = {},
  clrRoster = [],
  clrStartIndex = 0,
  clrAnchorDate,
}) => {
  const schedule = [];
  roster = normalizeRoster(roster);
  if (!roster || !scheduleStart) return schedule;

  const start = parseDate(scheduleStart);
  if (Number.isNaN(start.getTime())) return schedule;

  const ordered = sortZones(zones, anchorZone);
  const blockCount = getBlockSizes(blocksPerWeek).length;

  // Align to the first Monday on or after the start date.
  const firstMonday = firstMondayOnOrAfter(scheduleStart);

  for (let w = 0; w < weeksToShow; w++) {
    const weekStart = new Date(firstMonday);
    weekStart.setUTCDate(firstMonday.getUTCDate() + w * 7);

    const weekData = {
      weekNum: w + 1,
      startDate: formatDate(weekStart),
      days: [],
      assignments: {},
    };

    ordered.forEach((zone) => {
      const members = roster?.[zone.name] || [];
      if (members.length === 0) {
        weekData.assignments[zone.name] = new Array(blockCount).fill('—');
        return;
      }
      const pattern = getRotationPattern(members.length, blocksPerWeek);
      const weekPattern = pattern[w % pattern.length];
      weekData.assignments[zone.name] = weekPattern.map((idx) => members[idx] || '—');
    });

    for (let d = 0; d < 7; d++) {
      const dayDate = new Date(weekStart);
      dayDate.setUTCDate(weekStart.getUTCDate() + d);
      const dateStr = formatDate(dayDate);
      const dayOfWeek = dayDate.getUTCDay();
      const dayIndex = dayIndexInWeek(dayOfWeek);
      const block = getBlockForDayIndex(dayIndex, blocksPerWeek);

      const dayData = {
        date: dateStr,
        dayName: DAY_NAMES[dayOfWeek],
        isWeekend: dayIndex >= 5,
        block,
        people: {},
        basePerson: {},
        hasPto: {},
        hasOverride: {},
      };

      ordered.forEach((zone) => {
        const overrideKey = `${dateStr}-${zone.name}`;
        const baseAssignment = weekData.assignments[zone.name][block];

        if (overrides[overrideKey]) {
          dayData.people[zone.name] = overrides[overrideKey];
          dayData.basePerson[zone.name] = baseAssignment;
          dayData.hasOverride[zone.name] = true;
        } else {
          dayData.people[zone.name] = baseAssignment;
        }

        if (ptoEntries.some((p) => p.name === dayData.people[zone.name] && p.date === dateStr)) {
          dayData.hasPto[zone.name] = true;
        }
      });

      weekData.days.push(dayData);
    }

    // CLR: one person per week, straight round-robin. Holding a shift and CLR
    // in the same week is allowed, so shift duty does not affect the pick.
    // With an anchor date, week 0 of the rotation is fixed to an absolute Monday
    // so the CLR does not shift when the view's Start date changes. Without one,
    // fall back to the legacy (startIndex + weekIndex-from-view) rotation.
    let rotatedClr = null;
    if (clrRoster.length) {
      const n = clrRoster.length;
      const offset = clrAnchorDate
        ? Math.round((weekStart.getTime() - parseDate(clrAnchorDate).getTime()) / (7 * 86_400_000))
        : clrStartIndex + w;
      rotatedClr = clrRoster[((offset % n) + n) % n];
    }
    const clrSwap = overrides[clrOverrideKey(weekData.startDate)];
    weekData.clr = clrSwap || rotatedClr;
    if (clrSwap) {
      weekData.clrBase = rotatedClr;
      weekData.clrOverridden = true;
    }

    schedule.push(weekData);
  }

  return schedule;
};

// Markers a sync appends to an SLR title after the zone, last one first.
const SLR_TITLE_MARKS = [' ⚠️PTO', ' 🌟'];

const slrTitleZone = (summary) => {
  let title = summary;
  for (const mark of SLR_TITLE_MARKS) {
    if (title.endsWith(mark)) title = title.slice(0, -mark.length);
  }
  const match = /\(([^()]+)\)$/.exec(title);
  return match ? match[1] : null;
};

// An event time as an instant; an all-day date reads as UTC midnight.
const eventInstant = (time) => {
  if (time?.dateTime) return new Date(time.dateTime);
  if (time?.date) return new Date(`${time.date}T00:00:00Z`);
  return null;
};

/** Start and end instants of a calendar event or event body. */
export const calendarEventBounds = (event) => ({
  start: eventInstant(event?.start),
  end: eventInstant(event?.end),
});

/** Whether an event starts in [start, end) and ends after `since`. */
export const inCalendarSyncScope = (event, { start, end, since }) => {
  const bounds = calendarEventBounds(event);
  return !!bounds.start && !!bounds.end && bounds.start >= start && bounds.start < end && bounds.end > since;
};

/** The slot an event fills (SLR: zone and start, CLR: week start), or null for any other title. */
export const calendarEventKey = (event) => {
  const summary = event?.summary || '';
  if (summary.startsWith('SLR: ')) {
    const zone = slrTitleZone(summary);
    const start = eventInstant(event.start);
    if (!zone || !start || Number.isNaN(start.getTime())) return null;
    return `slr|${zone}|${start.toISOString()}`;
  }
  if (summary.startsWith('CLR: ')) {
    return event.start?.date ? `clr|${event.start.date}` : null;
  }
  return null;
};

// Attendee emails, organizer excluded.
const attendeeEmails = (event) =>
  (event?.attendees || [])
    .filter((attendee) => !attendee.organizer)
    .map((attendee) => String(attendee.email || '').toLowerCase())
    .filter(Boolean)
    .sort()
    .join(',');

const sameEventTime = (a, b) => {
  const x = eventInstant(a);
  const y = eventInstant(b);
  return !!x && !!y && x.getTime() === y.getTime() && !!a?.date === !!b?.date;
};

const sameCalendarEvent = (want, have) =>
  want.summary === (have.summary || '') &&
  (want.description || '') === (have.description || '') &&
  sameEventTime(want.start, have.start) &&
  sameEventTime(want.end, have.end) &&
  String(want.colorId || '') === String(have.colorId || '') &&
  attendeeEmails(want) === attendeeEmails(have);

/** Writes that turn `existing` into `desired`: create (bodies), update and unchanged ({ id, body }), delete (events). */
export const planCalendarSync = (desired = [], existing = []) => {
  const byKey = new Map();
  const remove = [];
  for (const event of existing) {
    const key = calendarEventKey(event);
    if (!key || byKey.has(key)) remove.push(event);
    else byKey.set(key, event);
  }

  const wanted = new Set();
  const create = [];
  const update = [];
  const unchanged = [];
  for (const body of desired) {
    const key = calendarEventKey(body);
    if (!key) {
      create.push(body);
      continue;
    }
    if (wanted.has(key)) continue;
    wanted.add(key);
    const have = byKey.get(key);
    if (!have) create.push(body);
    else if (sameCalendarEvent(body, have)) unchanged.push({ id: have.id, body });
    else update.push({ id: have.id, body });
  }

  for (const [key, event] of byKey) {
    if (!wanted.has(key)) remove.push(event);
  }

  return { create, update, delete: remove, unchanged };
};
